// Migrated Header.vue from Vue 2 to Vue 3
<template>
    <header class="header" :class="{ 'header-hidden': isHeaderHidden }">
        <div class="hamburger" @click="toggle"></div>
        <div class="header-logo">
          <router-link to="/">
              <img src="../assets/logo_sector.svg" class="logo" />
          </router-link>
        </div>
        <nav class="header-nav">
            <ul>
                <li>
                    <router-link :to="{ name: 'contents', params: { year: selectedYear } }">How to use</router-link>
                </li>
                <li>
                    <router-link :to="{ name: 'respondents', params: { year: selectedYear } }">List of respondents</router-link>
                </li>
                <li>
                    <router-link :to="{ name: 'contact' }">Contact us</router-link>
                </li>
            </ul>
        </nav>

        <div class="header-controls">
            <div class="year-selector">
                <select :value="selectedYear" @change="handleYearChange">
                    <option v-for="year in availableYears" :key="year" :value="year">
                        {{ year }}
                    </option>
                </select>
            </div>
            <div class="search-trigger" @click="openSearch">
                <font-awesome-icon icon="magnifying-glass" />
            </div>
        </div>

        <!-- Search Modal -->
        <transition name="search-modal-anim">
            <div v-if="showSearchModal" class="search-modal-overlay" @click.self="closeSearch">
                <div class="search-modal">
                    <div class="modal-header">
                        <h3>Search for Charts</h3>
                        <button class="close-btn" @click="closeSearch" title="Close">
                            <font-awesome-icon icon="times" />
                        </button>
                    </div>
                    <div class="modal-body">
                        <input
                            v-model="searchQuery"
                            ref="searchInput"
                            type="text"
                            placeholder="Type to search..."
                            class="search-input"
                        />
                        <div class="scope-toggle">
                            <label>
                                <input type="radio" value="current" v-model="searchScope" />
                                Selected Year ({{ selectedYear }})
                            </label>
                            <label>
                                <input type="radio" value="all" v-model="searchScope" />
                                All Years
                            </label>
                        </div>
                        <div class="results-container">
                            <ul class="search-results" v-if="filteredCharts.length">
                                <li v-for="chart in filteredCharts" :key="chart.id + '-' + chart.year">
                                    <router-link :to="getChartLink(chart)" @click="handleResultClick">
                                        <div class="result-name">
                                            <span>{{ chart.name }}</span>
                                            <span class="result-year" v-if="searchScope === 'all'">{{ chart.year }}</span>
                                        </div>
                                        <div class="result-desc">{{ chart.description }}</div>
                                    </router-link>
                                </li>
                            </ul>
                            <p v-else-if="searchQuery" class="no-results">No matches found.</p>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

    </header>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { useYear } from '../composables/useYear';

interface ChartItem {
    id: number;
    name: string;
    pathname: string;
    description?: string;
    year?: string; // Augmented during search
    category?: string;
    exclude?: boolean;
}

const emit = defineEmits(['toggle', 'close-menu']);
const isActive = ref(false);

const { selectedYear, availableYears } = useYear();
const router = useRouter();


// Search State
const showSearchModal = ref(false);
const searchQuery = ref('');
const searchScope = ref<'current' | 'all'>('current'); // 'current' or 'all'
// searchIndex maps Year -> List of Charts
const searchIndex = ref<Record<string, ChartItem[]>>({});
const searchInput = ref<HTMLInputElement | null>(null);

function toggle() {
    isActive.value = !isActive.value;
    emit('toggle');
}

const handleYearChange = (event: Event) => {
    const newYear = (event.target as HTMLSelectElement).value;

    // Always navigate to 'contents' (How to use) page when year is changed
    router.push({ name: 'contents', params: { year: newYear } });
};

const openSearch = () => {
    emit('close-menu');
    showSearchModal.value = true;
    document.documentElement.style.setProperty('overflow', 'hidden', 'important');
    document.body.style.setProperty('overflow', 'hidden', 'important');
    window.addEventListener('keydown', handleKeydown);
    nextTick(() => {
        searchInput.value?.focus();
    });
};

const closeSearch = () => {
    showSearchModal.value = false;
    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
    searchQuery.value = '';
    searchScope.value = 'current';
    window.removeEventListener('keydown', handleKeydown);
};

const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
        closeSearch();
    }
};

// Fetch index
onMounted(async () => {
    window.addEventListener('scroll', handleScroll);
    try {
        const promises = availableYears.map(async (year: string) => {
             try {
                const response = await axios.get<ChartItem[]>(`/data/data_${year}/all_charts.json`);
                return { year, data: response.data };
             } catch (e) {
                 console.warn(`Failed to load search index for ${year}`, e);
                 return { year, data: [] };
             }
        });

        const results = await Promise.all(promises);
        const index: Record<string, ChartItem[]> = {};
        results.forEach((res) => {
            index[res.year] = res.data;
        });
        searchIndex.value = index;
    } catch (e) {
        console.error('Failed to load search indexes', e);
    }
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
});

// Scroll Logic
const isHeaderHidden = ref(false);
let lastScrollY = 0;

const handleScroll = () => {
    if (showSearchModal.value) return;

    const currentScrollY = window.scrollY;

    // Check for mobile width OR landscape mobile height
    const isMobile = window.innerWidth <= 820 || (window.innerHeight <= 500 && window.innerWidth > 820 && window.screen.orientation.type.includes('landscape'));

    if (!isMobile) {
        isHeaderHidden.value = false;
        return;
    }

    if (currentScrollY > 90) { // Only hide after passing header height
        if (currentScrollY > lastScrollY) {
            // Scrolling down
            isHeaderHidden.value = true;
        } else {
            // Scrolling up
            isHeaderHidden.value = false;
        }
    } else {
        isHeaderHidden.value = false;
    }
    lastScrollY = currentScrollY;
};

const filteredCharts = computed(() => {
    if (!searchQuery.value) return [];
    const query = searchQuery.value.toLowerCase();
    const results: ChartItem[] = [];

    const yearsToSearch = searchScope.value === 'current' ? [selectedYear.value] : availableYears;

    yearsToSearch.forEach(year => {
        const yearCharts = searchIndex.value[year];
        if (yearCharts) {
            yearCharts.forEach((chart: ChartItem) => {
                 if (chart.exclude) return;

                 const nameMatch = chart.name && chart.name.toLowerCase().includes(query);
                 const descMatch = chart.description && chart.description.toLowerCase().includes(query);

                 if (nameMatch || descMatch) {
                    results.push({
                        ...chart,
                        year: year // Attach year for display/link
                    });
                }
            });
        }
    });

    return results;
});

const getChartLink = (chart: ChartItem) => {
    const year = chart.year || selectedYear.value;
    return { name: 'chart', params: { pathname: chart.pathname, year: year } };
};

const handleResultClick = () => {
    closeSearch();
};
</script>

<style lang="scss" scoped>
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      color: var(--lcp-off-white);
      position: relative;
    }
    h1,
    a {
        line-height: 28px;
        color: var(--header-font-colour);
        font-family: var(--montserrat);
        font-weight: 700;
        font-size: 0.9rem;
    }
    h1 {
        padding: 11px 0 11px 11px;
        float: left;
    }
    img {
        float: right;
        padding: 5px 13px 10px 25px;
        min-width: 150px;
    }
    .logo {
      filter: brightness(0) invert(1);
    }
    .header-nav {
        padding: 11px 20px;

        ul {
            list-style: none;
            margin: 0;
            padding: 0;
            display: flex;
            gap: 20px;
            align-items: center;
        }

        li {
            margin: 0;
        }

        a {
            color: var(--header-font-colour);
            text-decoration: none;
            font-family: var(--montserrat);
            font-weight: 600;
            font-size: 1rem;
            transition: color 0.3s ease;

            &:hover {
                color: var(--toggle-background-active, #ccc);
            }

            &.router-link-active {
                color: var(--toggle-background, #007acc);
            }
        }
    }

    .year-selector {
        margin-right: 15px;
        select {
            background: transparent;
            color: var(--header-font-colour);
            font-family: var(--montserrat);
            font-weight: 700;
            font-size: 1rem;
            border: 1px solid var(--header-font-colour);
            padding: 5px 10px;
            margin-right: 10px;
            border-radius: 4px;
            cursor: pointer;

            option {
                color: black;
            }
        }
    }

    .header-controls {
        display: flex;
        align-items: center;
    }

    .header.active {
      .hamburger{
        &::before {
            content: "\f00d"; /* fa-times (close/x) */
            transform: rotate(180deg);
        }
      }
    }
    .hamburger {
        position: relative;
        width: 50px;
        height: 50px;
        display: none;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        background: var(--investment-violet);
        transition: all 0.3s ease;

        &::before {
            font-family: var(--font-awesome-font);
            font-weight: 900;
            content: "\f0c9"; /* fa-bars (hamburger) */
            color: var(--lcp-off-white);
            font-size: 1.2rem;
            display: inline-block;
            text-rendering: auto;
            -webkit-font-smoothing: antialiased;
            transition: all 0.3s ease;
            transform: rotate(0deg);
        }
    }

    @include mobile-all {
        .header {
            justify-content: flex-start;
            position: fixed; /* Fixed on mobile */
            top: 0;
            width: 100%;
            z-index: 999;
            background: var(--header-background); /* Ensure opacity */
            transition: transform 0.3s ease-in-out;
        }
        .header.header-hidden {
            transform: translateY(-100%);
        }
        h1 {
            padding: 11px 0 11px 0;
        }
        .hamburger {
            display: flex;
            float: left;
            height: 90px;
            width: 50px;
            background: var(--investment-violet);
        }
        .fa-icon {
            width: auto;
            height: 1.2rem;
        }
        .header-nav {
            display: none; /* Hide in mobile portrait */
        }
        .year-selector {
            position: absolute;
            right: 60px; /* Left of search icon */
            top: 50%;
            transform: translateY(-50%);
            margin-right: 0;
        }
    }

    // Search Styles
    .search-trigger {
        cursor: pointer;
        padding: 10px;
        color: var(--lcp-off-white);
        font-size: 1.2rem;
        transition: color 0.3s;
        margin-left: 10px;
        margin-right: 20px; /* Space for hamburger on mobile or layout balance */
    }
    .search-trigger:hover {
        color: var(--toggle-background-active, #ccc);
    }

    /* Modal Overlay */
    .search-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100dvh;
        background: rgba(0, 0, 0, 0.7);
        z-index: 1000;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    /* Transition Styles */
    .search-modal-anim-enter-active,
    .search-modal-anim-leave-active {
        transition: opacity 0.3s ease;
    }

    .search-modal-anim-enter-from,
    .search-modal-anim-leave-to {
        opacity: 0;
    }

    .search-modal-anim-enter-active .search-modal,
    .search-modal-anim-leave-active .search-modal {
        transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); /* Bouncy zoom */
    }

    .search-modal-anim-enter-from .search-modal,
    .search-modal-anim-leave-to .search-modal {
        transform: scale(0.9);
    }

    /* Modal Window */
    .search-modal {
        background: white;
        width: 75%;
        height: 75%;
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-shadow: 0 10px 25px rgba(0,0,0,0.5);
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px 20px;
        background: var(--toggle-background-active);
        color: white;
    }

    .modal-header h3 {
        margin: 0;
        font-family: var(--montserrat);
    }

    .close-btn {
        background: none;
        border: none;
        color: white;
        font-size: 1.5rem;
        cursor: pointer;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .close-btn:hover {
        background: rgba(255,255,255,0.2);
        border-radius: 4px;
    }

    .modal-body {
        padding: 20px;
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    .search-input {
        max-width: 100%;
        padding: 15px;
        font-size: 1.2rem;
        border: 2px solid #ddd;
        border-radius: 8px;
        margin-bottom: 20px;
        font-family: var(--montserrat);
    }
    .search-input:focus {
        border-color: var(--lcp-light-blue);
        outline: none;
    }

    .scope-toggle {
        display: flex;
        gap: 20px;
        margin-bottom: 20px;
        font-family: var(--montserrat);

        label {
            display: flex;
            flex: 1 1 50%;
            align-items: center;
            vertical-align: middle;
            gap: 8px;
            cursor: pointer;
            font-size: 0.9rem;
            line-height: 1.2rem;
            color: var(--lcp-black);
        }

        input[type="radio"] {
            cursor: pointer;
            margin: 0;
        }
    }

    .results-container {
        flex: 1;
        overflow-y: auto;
    }

    .search-results {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    .search-results li {
        margin-bottom: 15px;
    }

    .search-results a {
        display: block;
        padding: 15px;
        background: #f5f5f5;
        border-radius: 6px;
        text-decoration: none;
        color: #333;
        transition: background 0.2s;
    }
    .search-results a:hover {
        background: #e0e0e0;
    }

    .result-name {
        font-weight: bold;
        font-size: 1.1rem;
        color: var(--lcp-dark-blue, #17355e);
        margin-bottom: 5px;
        display: flex;
        gap: 15px;
        justify-content: space-between;
    }

    .result-year {
        display: inline-block;
        background: var(--lcp-light-blue);
        color: var(--lcp-dark-blue);
        font-weight: bold;
    }

    .result-desc {
        font-size: 0.9rem;
        color: #666;
    }

    .no-results {
        text-align: center;
        color: #999;
        font-size: 1.2rem;
        margin-top: 20px;
    }

    @include mobile-all {
        .search-modal {
            width: 95%;
            height: 95%;
            max-height: 95dvh;
        }
        .search-trigger {
            position: absolute;
            right: 20px; /* Moved further right (was 60px) */
            top: 50%; /* Vertically centered */
            transform: translateY(-50%);
            margin: 0;
            font-size: 1.5rem; /* Larger icon */
        }
    }

    @media only screen and (min-width: 768px) {
        .scope-toggle {
          label {
            flex: none;
          }
      }
    }
</style>
